import React, {useState, useEffect} from 'react';
import {
  ScrollView,
  View,
  Text,
  TouchableOpacity,
  StatusBar,
  SafeAreaView,
  TextInput,
  FlatList,
  ActivityIndicator,
  Platform,
} from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import LinearGradient from 'react-native-linear-gradient';
import styles from './styles';
import Bold from '../../../typography/BoldText';
import Regular from '../../../typography/RegularText';
import Medium from '../../../typography/MediumText';
import { generateCalendarDevotions } from '../../../util/devotionsData';
import ApiService from '../../../util/apiService';
import colors from '../../../util/colors';

const AllDevotionsScreen = ({navigation}) => {
  const [allDevotions, setAllDevotions] = useState([]);
  const [filteredDevotions, setFilteredDevotions] = useState([]);
  const [displayedDevotions, setDisplayedDevotions] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    loadDevotions();
  }, []);

  useEffect(() => {
    // Update displayed devotions when filtered devotions change
    const startIndex = 0;
    const endIndex = pageSize;
    setDisplayedDevotions(filteredDevotions.slice(startIndex, endIndex));
    setCurrentPage(1);
    setHasMoreData(filteredDevotions.length > pageSize);
  }, [filteredDevotions]);

  const loadDevotions = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading previous devotions from API...');
      
      const devotions = await ApiService.getPreviousDevotions();
      console.log('📊 Loaded previous devotions from API:', devotions.length);
      setAllDevotions(devotions);
      setFilteredDevotions(devotions);
    } catch (error) {
      console.error('❌ Error loading previous devotions from API:', error);
      setError('Failed to load previous devotions. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreDevotions = () => {
    if (loadingMore || !hasMoreData) return;
    
    setLoadingMore(true);
    const nextPage = currentPage + 1;
    const startIndex = (nextPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    
    const newDevotions = filteredDevotions.slice(startIndex, endIndex);
    setDisplayedDevotions(prev => [...prev, ...newDevotions]);
    setCurrentPage(nextPage);
    setHasMoreData(endIndex < filteredDevotions.length);
    setLoadingMore(false);
  };

  const loadCalendarDevotions = async () => {
    try {
      const calendarDevotions = await generateCalendarDevotions();
      // setCalendarDevotions(calendarDevotions); // This state variable doesn't exist in the original file
    } catch (error) {
      console.error('Error loading calendar devotions:', error);
    }
  };

  const handleSearch = (text) => {
    setSearchText(text);
    applyFilters(text, activeFilter);
  };

  const applyFilters = (search = searchText, filter = activeFilter) => {
    let filtered = allDevotions;

    // Apply search filter
    if (search.trim() !== '') {
      filtered = filtered.filter(devotion =>
        devotion.title.toLowerCase().includes(search.toLowerCase()) ||
        devotion.text.toLowerCase().includes(search.toLowerCase())
      );
    }

    // Apply category filter
    switch (filter) {
      case 'recent':
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        filtered = filtered.filter(devotion => new Date(devotion.date) >= oneWeekAgo);
        break;
      case 'favorites':
        // For now, we'll show all devotions. In a real app, you'd have a favorites system
        filtered = filtered.slice(0, 10); // Show first 10 as "favorites"
        break;
      case 'this-month':
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        filtered = filtered.filter(devotion => {
          const devotionDate = new Date(devotion.date);
          return devotionDate >= startOfMonth && devotionDate <= endOfMonth;
        });
        break;
      default:
        // 'all' - no additional filtering
        break;
    }

    setFilteredDevotions(filtered);
  };

  const handleFilterPress = (filter) => {
    setActiveFilter(filter);
    applyFilters(searchText, filter);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const renderDevotionItem = ({item, index}) => {
    console.log(`Rendering devotion ${index + 1}:`, item.title);
    return (
    <TouchableOpacity 
      style={styles.devotionItemContainer}
      onPress={() => {
        if (Platform.OS === 'web') {
          navigation.navigate('Home', { screen: 'DailyDevotion', params: { selectedDate: item.date } });
        } else {
          navigation.navigate('HomeRoot', {screen: 'DailyDevotion', params: {selectedDate: item.date}})
        }
      }}
      activeOpacity={0.8}
    >
      <LinearGradient
        colors={['#6C4D2A', '#B09569', '#D0B277']}
        start={{x: 0, y: 0}}
        end={{x: 1, y: 1}}
        style={styles.devotionGradient}
      >
        {/* Dark overlay for better text readability */}
        <View style={styles.darkOverlay} />
        
        {/* Decorative corner elements */}
        <View style={styles.cornerDecoration}>
          <Text style={styles.cornerText}>✝</Text>
        </View>
        
        <View style={styles.devotionContent}>
          <View style={styles.devotionHeader}>
            <Medium style={styles.devotionDate}>{formatDate(item.date)}</Medium>
            <View style={styles.devotionNumber}>
              <Text style={styles.devotionNumberText}>#{item.id}</Text>
            </View>
          </View>
          
          <Bold style={styles.devotionTitle}>{item.title}</Bold>
          
          <Regular style={styles.devotionPreview}>
            {item.text.substring(0, 120)}...
          </Regular>
          
          <View style={styles.devotionFooter}>
            <View style={styles.readMoreContainer}>
              <Medium style={styles.readMoreText}>Read Full Devotion</Medium>
              <Icon name="arrow-forward" size={16} color="#FFFFFF" />
            </View>
          </View>
        </View>
      </LinearGradient>
    </TouchableOpacity>
    );
  };

  const renderFilterButton = (filter, label, icon) => (
    <TouchableOpacity
      style={[
        styles.filterButton,
        activeFilter === filter && styles.filterButtonActive
      ]}
      onPress={() => handleFilterPress(filter)}
    >
      {icon}
      <Medium style={[
        styles.filterButtonText,
        activeFilter === filter && styles.filterButtonTextActive
      ]}>
        {label}
      </Medium>
    </TouchableOpacity>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Medium style={styles.loadingText}>Loading previous devotions...</Medium>
        </View>
      );
    }

    if (error) {
      return (
        <View style={styles.errorContainer}>
          <Icon name="cloud-offline" size={48} color={colors.lion} />
          <Bold style={styles.errorTitle}>Connection Error</Bold>
          <Regular style={styles.errorText}>{error}</Regular>
          <TouchableOpacity style={styles.retryButton} onPress={loadDevotions}>
            <Medium style={styles.retryButtonText}>Try Again</Medium>
          </TouchableOpacity>
        </View>
      );
    }

    return (
      <>
        {/* Devotions List */}
        <FlatList
          data={displayedDevotions}
          renderItem={renderDevotionItem}
          keyExtractor={(item) => item.id.toString()}
          style={styles.flatList}
          contentContainerStyle={styles.listContainer}
          showsVerticalScrollIndicator={false}
          numColumns={1}
          initialNumToRender={10}
          maxToRenderPerBatch={10}
          windowSize={10}
          onEndReached={loadMoreDevotions}
          onEndReachedThreshold={0.1}
          ListFooterComponent={() => (
            hasMoreData ? (
              <View style={styles.loadMoreContainer}>
                {loadingMore ? (
                  <ActivityIndicator size="small" color={colors.primary} />
                ) : (
                  <TouchableOpacity 
                    style={styles.loadMoreButton}
                    onPress={loadMoreDevotions}
                  >
                    <Medium style={styles.loadMoreText}>Load More Devotions</Medium>
                  </TouchableOpacity>
                )}
              </View>
            ) : null
          )}
        />
      </>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#6C4D2A" />
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Icon name="arrow-back" size={24} color="#EFE5D7" />
        </TouchableOpacity>
        <Bold style={styles.headerTitle}>Previous Devotions</Bold>
        <View style={styles.headerRight} />
      </View>
      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <TextInput
          style={styles.searchInput}
          placeholder="Search devotions..."
          placeholderTextColor={colors.lion}
          value={searchText}
          onChangeText={handleSearch}
        />
        <Icon name="search" size={20} color={colors.lion} style={styles.searchIcon} />
      </View>
      {/* Filter Buttons */}
      <View style={styles.filterContainer}>
        <ScrollView 
          horizontal 
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.filterScrollContainer}
        >
          {renderFilterButton('all', 'All', <Icon name="apps" size={16} color={activeFilter === 'all' ? colors.antiqueWhite : colors.kobicha} />)}
          {renderFilterButton('recent', 'Recent', <Icon name="time" size={16} color={activeFilter === 'recent' ? colors.antiqueWhite : colors.kobicha} />)}
          {renderFilterButton('this-month', 'This Month', <Icon name="calendar" size={16} color={activeFilter === 'this-month' ? colors.antiqueWhite : colors.kobicha} />)}
        </ScrollView>
      </View>
      {/* Content */}
      <View style={{flex: 1, minHeight: 400}}>
        {renderContent()}
      </View>
    </SafeAreaView>
  );
};

export default AllDevotionsScreen;